import * as React from 'react';
import { WarningAmber } from '@mui/icons-material';
import {
  alpha,
  Checkbox,
  Chip,
  CircularProgress,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';

const filter = createFilterOptions({
  stringify: (option) => option.value + option.label,
});

export default function ContactAutocomplete({
  onBlur,
  onChipDelete,
  options,
  ...props
}) {
  const theme = useTheme();

  const getChipIcon = (option) => {
    if (option.status && option.status === 'unverified')
      return <CircularProgress size={18} color="primary" />;

    if (
      option.status &&
      (option.status === 'invalid' || option.status === 'blacklisted')
    ) {
      return (
        <Tooltip
          title={`Number is ${option.status}. Message won't be sent to this number.`}
          sx={{ '&.MuiChip-icon': { color: 'error.main', fontSize: 18 } }}
        >
          <WarningAmber />
        </Tooltip>
      );
    }
    return null;
  };

  return (
    <Autocomplete
      options={options.map((option, index) => ({
        ...option,
        key: index,
      }))}
      slotProps={{
        popper: {
          sx: {
            border: `2px solid`,
            borderColor: 'secondary.light',
            borderRadius: 2,
            overflow: 'hidden',
          },
        },
        listbox: {
          sx: {
            padding: 0,
          },
        },
      }}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      freeSolo
      multiple
      clearOnBlur
      disableCloseOnSelect
      handleHomeEndKeys
      getOptionLabel={(option) => {
        // Value selected with enter, right from the input
        if (typeof option === 'string') {
          return option;
        }
        // Add "xxx" option created dynamically
        if (option.inputValue) {
          return option.inputValue;
        }
        // Regular option
        return option.label;
      }}
      renderValue={(value, getItemProps) =>
        value.map((option, index) => (
          <Chip
            {...getItemProps({ index })}
            key={`${option.value}_${index}`}
            label={option.label || option.value}
            icon={getChipIcon(option)}
          />
        ))
      }
      renderOption={(props, option, { selected }) => (
        <ListItem
          {...props}
          key={`${option.key}`}
          sx={{
            padding: 0,
            '&:nth-of-type(even)': {
              backgroundColor: alpha(theme.palette.secondary.light, 0.15),
            },
          }}
        >
          {/* Hidden per request, will reintroduce in future when needed
          <ListItemIcon>
            <Checkbox sx={{ color: 'secondary.main' }} checked={selected} />
          </ListItemIcon> */}
          <ListItemText sx={{ padding: 0 }}>
            <Stack
              sx={{ flexDirection: 'row', justifyContent: 'space-between' }}
            >
              <Typography variant="span">{option.label}</Typography>
              <Typography variant="span">
                {option.count ? option.type || 'N/A' : option.value}
              </Typography>
            </Stack>
          </ListItemText>
        </ListItem>
      )}
      filterOptions={(options, params) => {
        const filtered = filter(options, params);

        const { inputValue } = params;
        // Suggest the creation of a new value
        const isExisting = options.some(
          (option) => inputValue === option.value
        );
        if (inputValue !== '' && !isExisting) {
          filtered.push({
            label: `${inputValue}`,
            value: inputValue,
            status: 'unverified',
            type: 'manual',
          });
        }

        return filtered;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          placeholder="Select..."
          onBlur={onBlur}
          autoFocus
          sx={{
            '& .MuiAutocomplete-input': {
              '&::placeholder': {
                color: 'grey',
              },
            },
          }}
        />
      )}
      loadingText="Loading..."
      {...props}
    />
  );
}
